import * as Quill from 'quill';
import { saveAndGetImgURL } from '@store/firebase/storage';
import validateFile from '@utils/validateFile';
import { getEmoticon } from '@store/firebase/emoticon/get-emotion';
import { getMembers } from '@store/firebase/user/getUsers';


// 파일 업로더 ----------------------------------------------
export function fileUploadHandler({ setFileList, folderPath }) {
  const input = document.createElement('input');
  input.setAttribute('type', 'file');
  input.setAttribute('accept', '*/*');
  input.setAttribute('multiple', 'true');
  input.click();

  input.onchange = async () => {
    const files = input.files;

    if (files) {
      const selectedFiles = Array.from(files);

      Promise.allSettled(
        selectedFiles.map(async (file, i) => {
          validateFile(file.type, file.size, file.name);
          const fileURL = await saveAndGetImgURL(folderPath, file);
          const fileData = {
            name: file.name,
            url: fileURL,
          };

          setFileList((prev: any) => [...prev!, fileData]);
        }),
      );
    }
  };
}

// 이미지 업로더 ---------------------------------------------------
type ImgHandlerProps = {
  quill: any; // Quill instance
  imgURLList?: any;
  setImgURLList?: any;
  collectionPath: string;
  limitFetch?: number;
};

export function imageHandler({
  quill,
  setImgURLList,
  imgURLList,
  collectionPath,
  limitFetch = 20,
}: ImgHandlerProps) {
  const input = document.createElement('input');
  input.setAttribute('type', 'file');
  input.setAttribute('accept', 'image/*');
  input.setAttribute('multiple', 'true');
  input.click();

  input.onchange = async () => {
    const files = input.files;
    // 한 번에 선택 가능한 갯수 제한
    if (files && files.length > limitFetch) {
      alert(`이미지 첨부는 최대 ${limitFetch}개까지 가능합니다.`);
      return;
    }

    if (files) {
      try {
        const selectedFiles = Array.from(files);
        const cursorPosition = quill.getSelection(true)
          ? quill.getSelection(true).index
          : 0;

        // ✅ 파일 순서를 보장하기 위해 for...of 사용
        for (let i = 0; i < selectedFiles.length; i++) {
          const file = selectedFiles[i];

          validateFile(file.type, file.size, file.name);

          // 이미지 URL을 업로드하고 받아오기 (비동기 처리)
          const imgURL: string = await saveAndGetImgURL(collectionPath, file);

          if (setImgURLList) setImgURLList((prev: any) => [...prev!, imgURL]);

          // 이미지 삽입 및 커서 위치 설정
          quill.insertEmbed(cursorPosition + i, 'image', imgURL);
          quill.setSelection(cursorPosition + i + 1);
        }

        // removeImgLoadingBox();
      } catch (err) {
        alert(err.message);
      }
    }
  };
}

// 동영상 직접 업로드 ----------------------------------------------
type VideoUploadHandlerProps = {
  quill: any;
  setVideoURLList?: (updater: (prev: string[]) => string[]) => void;
  collectionPath: string;
};

export async function videoUploadHandler({
  quill,
  setVideoURLList,
  collectionPath,
}: VideoUploadHandlerProps): Promise<void> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute(
      'accept',
      'video/mp4,video/quicktime,video/x-msvideo,video/x-matroska,video/webm,video/x-m4v,audio/mpeg',
    );

    // 파일 다이얼로그를 닫으면(취소 포함) window가 focus를 되찾음
    // → 파일이 선택되지 않은 경우 Promise를 종료
    const onWindowFocus = () => {
      setTimeout(() => {
        if (!input.files || input.files.length === 0) {
          resolve();
        }
      }, 300);
      window.removeEventListener('focus', onWindowFocus);
    };
    window.addEventListener('focus', onWindowFocus);

    input.click();

    input.onchange = async () => {
      window.removeEventListener('focus', onWindowFocus);

      const files = input.files;
      if (!files || files.length === 0) {
        resolve();
        return;
      }

      const file = files[0];

      try {
        validateFile(file.type, file.size, file.name, 500);

        const cursorPosition = quill.getSelection(true)?.index ?? 0;

        const videoURL: string = await saveAndGetImgURL(collectionPath, file);

        if (setVideoURLList) {
          setVideoURLList((prev: string[]) => [...prev, videoURL]);
        }

        quill.insertEmbed(cursorPosition, 'videoUpload', videoURL);
        quill.setSelection(cursorPosition + 1);
      } catch (err) {
        alert(err.message);
      }

      resolve();
    };
  });
}

// 이모지 컨트롤
export const emojiHandler = ({ quill }) => {
  if (quill.current) {
    // 현재 커서 위치를 가져옵니다.
    const cursorPosition = quill.current.getEditor().getSelection().index;
    // 이모지를 현재 커서 위치에 삽입합니다.
    quill.current.getEditor().insertText(cursorPosition, '😀');
  }
};

async function suggestResult(list, searchTerm) {
  return list.filter((person) => {
    if (!person.value) return '';
    if (person.value.includes(searchTerm)) return person.value;
  });
}

export const mentionModule = {
  mentionDenotationChars: ['@'],
  blotName: 'mention',
  source: async function (searchTerm, renderList) {
    const members = await getMembers();
    const matchedPeople = await suggestResult(members, searchTerm);
    renderList(matchedPeople);
  },
};

export const customEmojiModule = {
  mentionDenotationChars: ['/'],
  blotName: 'custom-emoji',
  source: async function (searchTerm, renderList) {
    // do something
    const emoticons = await getEmoticon();
    const matchedEmoticon = await suggestResult(emoticons, searchTerm);
    renderList(matchedEmoticon);
  },
};

export const customHrHandler = ({ quill }) => {
  const range = quill.getSelection();

  if (range) {
    // HR 태그 삽입
    quill.insertEmbed(range.index, 'hr', {});
  }
};
